import { useCallback, useState } from 'react';
import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import { useDispatch } from 'store';
import { DispatchSnackbarService } from 'services';
import { useCheckoutActions } from '../store';

const service = new DispatchSnackbarService();

type Params = {
  /** False when there is nothing to act on (e.g. no subscription id) — `run` becomes a no-op. */
  ready: boolean;
  /** The billing call itself; everything around it is this hook's job. */
  perform: (client: any, userId: string) => Promise<unknown>;
  successMessage: string;
  errorMessage: string;
};

export type SubscriptionAction = {
  open: boolean;
  busy: boolean;
  run: () => Promise<void>;
  openModal: () => void;
  close: () => void;
};

/**
 * One confirmable billing call: busy state, the optional confirm modal, the snackbar and
 * the billing refresh. grpc-web puts the readable text in `details`, so that wins.
 */
export function useSubscriptionAction({ ready, perform, successMessage, errorMessage }: Params): SubscriptionAction {
  const { user } = useAuth() as any;
  const getBillingClient = useBillingClient();
  const actions = useCheckoutActions();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    if (!ready || !user?.tcId) return;
    setBusy(true);
    try {
      const client = await getBillingClient();
      await perform(client, user.tcId);
      service.dispatchSnackbar(dispatch, successMessage, 'alert', 'success', 5000);
      setOpen(false);
      await actions.refreshBilling();
    } catch (err: any) {
      service.dispatchSnackbar(dispatch, err?.details || err?.message || errorMessage, 'alert', 'error', 5000);
    } finally {
      setBusy(false);
    }
  }, [ready, perform, user?.tcId, getBillingClient, successMessage, errorMessage, actions, dispatch]);

  const close = useCallback(() => {
    if (!busy) setOpen(false);
  }, [busy]);

  const openModal = useCallback(() => setOpen(true), []);

  return { open, busy, run, openModal, close };
}
